/**
 * 手牌结算的**纯派生**：把 `PublicHandResult` 压成结算卡要显示的几行字。
 *
 * 单独成文件是为了能测：结算卡是唯一一处「钱对不对」直接呈现给玩家的地方，
 * 筹码守恒（Σ contributed === Σ won）必须有测试钉住，否则一个求和写错就成了
 * 「引擎算对了、UI 显示错了」这种最难排查的缺陷。
 */
import type { Chips, PublicHandState, SeatIndex, ShowdownEntry } from '@poker/shared'

/** Equal hand values share a rank. Hidden hands have no inferred rank. */
export function compareShowdown(entries: readonly ShowdownEntry[]) {
  const sorted = [...entries].sort((a,b) => (b.rank?.value ?? -1) - (a.rank?.value ?? -1) || a.revealOrder - b.revealOrder)
  let place = 0
  return sorted.map((entry,index) => {
    if (entry.mucked) return {entry, place:null}
    if (index === 0 || entry.rank.value !== sorted[index-1]?.rank?.value) place = index + 1
    return {entry, place}
  })
}

import { handLabel } from '../table/cardFormat.js'
import type { TableSeatInfo } from '../store/index.js'

export interface WinnerLine {
  readonly seat: SeatIndex
  readonly name: string
  /** 该座位从所有边池赢到的总额（不含未跟注退还） */
  readonly amount: Chips
  /** 中文牌型；未摊牌获胜时为 null */
  readonly handText: string | null
}

export interface HandSummary {
  readonly handNo: number
  /** 'fold' = 其他人全弃，不摊牌 */
  readonly endedBy: 'fold' | 'showdown'
  readonly winners: readonly WinnerLine[]
  /** 分池数 > 1 时值得提一句「边池」 */
  readonly potCount: number
  /** 未跟注退还：显示出来玩家才不会以为底池少算了一笔 */
  readonly uncalledReturn: { readonly name: string; readonly amount: Chips } | null
  /** 本手实际进入底池的总额 = Σ awards.amount。用于自检。 */
  readonly awardedTotal: Chips
}

function nameOf(seats: readonly TableSeatInfo[], seat: SeatIndex): string {
  return seats.find((s) => s.seat === seat)?.name ?? `座位 ${String(seat + 1)}`
}

/**
 * 合并同一座位在多个边池上的 award。
 *
 * 引擎按边池逐个派奖，一个人赢下主池和边池会产生两条 award；直接列出来会变成
 * 「张三 赢 $200 / 张三 赢 $60」，玩家读不出「一共赢了多少」。
 */
export function summarizeHand(
  view: PublicHandState | null,
  seats: readonly TableSeatInfo[],
): HandSummary | null {
  const result = view?.result
  if (view === null || result === undefined || result === null) return null

  const bySeat = new Map<SeatIndex, { amount: Chips; handText: string | null }>()
  for (const a of result.awards) {
    if (a.amount <= 0) continue
    const prev = bySeat.get(a.seat)
    const handText = a.handRank === null ? null : handLabel(a.handRank)
    bySeat.set(a.seat, {
      amount: (prev?.amount ?? 0) + a.amount,
      // 同一个人在不同边池用的是同一手牌，取第一个非 null 即可
      handText: prev?.handText ?? handText,
    })
  }

  const winners: WinnerLine[] = [...bySeat.entries()]
    .map(([seat, v]) => ({ seat, name: nameOf(seats, seat), amount: v.amount, handText: v.handText }))
    // 赢得多的排前面；同额按座位号，保证渲染顺序稳定（React key 不抖）
    .sort((a, b) => b.amount - a.amount || a.seat - b.seat)

  const ret = result.uncalledReturn
  return {
    handNo: result.handNo,
    endedBy: result.endedBy,
    winners,
    potCount: result.pots.length,
    uncalledReturn: ret === null ? null : { name: nameOf(seats, ret.seat), amount: ret.amount },
    awardedTotal: winners.reduce((n, w) => n + w.amount, 0),
  }
}

/**
 * 筹码守恒自检（红线 #19 的 UI 侧对应物）。
 * 返回 null 表示对得上；返回字符串表示哪一条对不上，dev 构建下直接弹 Toast。
 */
export function checkChipConservation(view: PublicHandState | null): string | null {
  const result = view?.result
  if (view === null || result === undefined || result === null) return null

  const sum = (rec: Readonly<Record<SeatIndex, number>>): number =>
    Object.values(rec).reduce((n, v) => n + v, 0)

  const contributed = sum(result.contributedBySeat)
  const won = sum(result.wonBySeat)
  if (contributed !== won) return `筹码不守恒：投入 ${String(contributed)} ≠ 赢得 ${String(won)}`

  const startTotal = sum(result.startingStacks)
  const endTotal = sum(result.finalStacks)
  if (startTotal !== endTotal) return `筹码不守恒：手牌前 ${String(startTotal)} ≠ 手牌后 ${String(endTotal)}`
  return null
}
